import { AdAccountPageType, DataFBQueryAdInterest, FacebookAdAccount, FacebookBusinessManager, FacebookInterestSearchData, FacebookPageType, FBLocation, FBPixel, InstagramPageType, InstagramPost, PixelConversionEvent } from 'modules/Types'
import FB, { FB_AD_ACCOUNT_STATUS, queryFB } from 'helpers/FB'
import Axios from 'helpers/Interceptor'
import Logger from 'Logger'
import * as Sentry from "@sentry/react";
import { healthCheckCampaign_api } from 'api/campaignApi'
import { getFbAccountDetails } from 'pages/MarketingPage/utils/fbCampaigns';
import dayjs from 'dayjs';

export type FacebookAdAccountAccess = FacebookAdAccount & {
  userHasAccess: boolean
  userHasPermissions: boolean
}

interface PlatformsIds {
  adAccountId?: string
  pixelId?: string
  businessId?: string
}

export type FacebookPermissions = {
  data?: {
    ad_account: boolean,
    business: boolean,
    pixel: boolean,
    errors: boolean,
  } | null;
  message?: string | null;
  isError?: boolean | null;
}

export const getFacebookPermissions_api = async ({ adAccountId, pixelId, businessId }: PlatformsIds): Promise<FacebookPermissions> => {
  try {
    const results = await Axios.get<FacebookPermissions>('/platform/facebook/permissions', {
      params: {
        ad_account: adAccountId,
        pixel: pixelId,
        business: businessId,
      },
    })

    return results.data
  } catch (error) {
    throw error
  }
}

export const getAdAccounts_api = async (
  businessManagerId?: string,
  access_token?: string
): Promise<FacebookAdAccountAccess[]> => {
  try {
    let queryUrl = `/me/adaccounts`
    if (businessManagerId) {
      queryUrl = `/${businessManagerId}/owned_ad_accounts`
    }

    const results = await queryFB(queryUrl, {
      params: {
        fields: 'amount_spent,name,id,account_status,user_tasks,business,currency,adspixels{name,id,is_unavailable}',
        limit: 250,
        access_token,
      },
    })

    const formattedAdAccounts = await Promise.all(
      results.data.map(async (account: FacebookAdAccount) => {
        const { user_tasks } = account

        let userHasCorrectAccess = false

        if (user_tasks) {
          try {
            const hasAnalyze: boolean = user_tasks.indexOf('ANALYZE') > -1
            const hasAdvertise: boolean = user_tasks.indexOf('ADVERTISE') > -1

            userHasCorrectAccess = hasAnalyze && hasAdvertise
          } catch (error) {
            Logger.error(error)
          }
        }

        let userHasPermissions = true;
        if (businessManagerId) {
          const permissionsResponse = await getFacebookPermissions_api({ businessId: businessManagerId, adAccountId: account.id })
          userHasPermissions = Boolean(permissionsResponse?.data?.ad_account)
        }

        return {
          ...account,
          amount_spent: account.amount_spent / 100,
          userHasAccess: userHasCorrectAccess,
          userHasPermissions,
        }
      })
    )

    const sortedAdAccounts = [...formattedAdAccounts].sort((a: any, b: any) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1
      }
      return 0
    }).sort((a: any, b: any) => {
      const accountAccessA = a.userHasAccess
      const accountAccessB = b.userHasAccess

      if (!accountAccessA && accountAccessB) {
        return 1
      }

      if (accountAccessA && !accountAccessB) {
        return -1
      }
      return 0
    }).sort((a: any, b: any) => {
      const accountStatusA = (FB_AD_ACCOUNT_STATUS as any)[a.account_status].valid
      const accountStatusB = (FB_AD_ACCOUNT_STATUS as any)[b.account_status].valid

      if (accountStatusB && !accountStatusA) {
        return 1
      }

      if (!accountStatusB && accountStatusA) {
        return -1
      }

      return 0
    })

    return sortedAdAccounts;
  } catch (error) {
    Logger.error(error)
    return []
  }
}

export const getBusinessManagers_api = async (access_token?: string): Promise<FacebookBusinessManager[]> => {
  try {
    const results = await queryFB(`/me/businesses`, {
      params: {
        fields: `id,name,profile_picture_uri,permitted_roles`,
        limit: 50,
        access_token,
      },
    })

    const sorted: FacebookBusinessManager[] = results.data
      .sort((a: any, b: any) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1
        }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1
        }
        return 0
      })
      .sort((a: any, b: any) => {
        if (
          b.permitted_roles.indexOf('ADMIN') > -1 &&
          a.permitted_roles.indexOf('ADMIN') > -1
        ) {
          return 0
        }

        if (
          b.permitted_roles.indexOf('ADMIN') > -1 &&
          a.permitted_roles.indexOf('ADMIN') === -1
        ) {
          return 1
        }

        if (
          b.permitted_roles.indexOf('ADMIN') === -1 &&
          a.permitted_roles.indexOf('ADMIN') > -1
        ) {
          return -1
        }

        return 0 // Default case
      })

    return sorted
  } catch (error) {
    return []
  }
}


export const createNewAdAccount_api = async (businessManager: FacebookBusinessManager, access_token?: string) => {
  try {
    const creationUrl = `/${businessManager.id}/adaccount`
    const creationPayload = {
      name: 'Symphony Ad Account',
      currency: 'USD',
      timezone_id: 1,
      media_agency: 'NONE',
      end_advertiser: businessManager.id,
      partner: process.env.REACT_APP_FACEBOOK_APP_ID,
      access_token,
    }

    const creationPromise = new Promise<FacebookAdAccountAccess>(
      async (resolve, reject) => {
        try {
          const response = await queryFB(creationUrl, {
            method: 'post',
            params: creationPayload,
          })

          if (!response || response.error) {
            Logger.error('Error response', response)
            reject(response.error)
          } else {
            resolve(response as FacebookAdAccountAccess)
          }
        } catch (error: any) {
          Logger.error('Error response', error)
          reject(error.error)
        }
      }
    )

    const createdAdAccount: FacebookAdAccountAccess = await creationPromise

    // once created, assign the current user as an admin to the
    // ad account ~ first find the "business user id"
    // by looping thru business users and finding user where
    // business.id matches current business id

    const getUserId = await queryFB('/me', {
      method: 'get',
      params: {
        fields: 'business_users',
      },
    })

    let userId = null

    const foundBusinessUser = (getUserId as any).business_users.data.find(
      (o: {
        id: string
        name: string
        business: {
          id: string
          name: string
        }
      }) => {
        return o.business.id === businessManager.id
      }
    )

    if (foundBusinessUser) {
      userId = foundBusinessUser.id
    } else {
      throw new Error('No business found for user')
    }

    const url = `/${createdAdAccount.id}/assigned_users`
    const payload = {
      user: userId,
      tasks: ['MANAGE', 'ADVERTISE', 'ANALYZE', 'DRAFT'],
    }

    await new Promise(async (resolve, reject) => {
      const response = await queryFB(url, {
        method: 'post',
        params: payload,
      })
      if (!response || response.error) {
        Logger.error('Error adding user to ad account', response)
        reject(response.error)
      } else {
        resolve(response)
      }
    })

    const { user_tasks } = createdAdAccount
    let userHasAccess = false

    if (user_tasks) {
      // check if they have:
      //  - ADVERTISE
      //  - ANALYZE
      const hasAnalyze: boolean = user_tasks.indexOf('ANALYZE') > -1
      const hasAdvertise: boolean = user_tasks.indexOf('ADVERTISE') > -1

      userHasAccess = hasAnalyze && hasAdvertise
    }

    return {
      ...createdAdAccount,
      name: 'Symphony Ad Account',
      userHasAccess,
    }
  } catch (error) {
    Logger.error('Error creating ad account', error)
    throw error;
  }
}

export const healthCheckCampaignFacebook_api = async (brandId: number, campaignId: number) => {
  try {
    const authResp = FB()?.getAuthResponse()
    await healthCheckCampaign_api(campaignId, authResp?.accessToken || undefined)
  } catch (error) {
    Sentry.captureException(`Error performing health check for campaign with ID: ${campaignId}. ERROR: ${error}`);
    throw error;
  }
}

export const isBusinessAdmin_api = async (businessId: string, access_token?: string) => {
  try {
    const response = await queryFB(`/${businessId}`, {
      params: {
        fields: 'business_users{id,name,business,role}',
        limit: 250,
        access_token,
      },
    })

    const businessUsers = (response as any).business_users.data

    const businessAdmins = businessUsers.filter((user: any) => {
      return user.role === 'ADMIN'
    })

    const personalBusinessUsersResponse = await queryFB('me', {
      params: {
        fields: 'business_users{id,name}',
      }
    })

    const personalBusinessUsers = (personalBusinessUsersResponse as any).business_users.data

    let isAdmin = false;
    for (const businessUser of businessAdmins) {
      const foundAdminUser = personalBusinessUsers.find((personalBusinessUser: any) => personalBusinessUser.id === businessUser.id)
      if (foundAdminUser) {
        isAdmin = true
        break
      }
    }

    return isAdmin
  } catch (error: any) {
    if (error?.error?.code === 104) return true;
    return false
  }
}

export const loggedInFb_api = async () => {
  try {
    const request = await Axios.get<{ logged: boolean, profile: any }>('/platform/facebook/me');
    const { logged, profile } = request.data;
    if (logged && profile) {
      return profile;
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
};

/** 
 * Fetches Facebook pages associated with a specific brand
 * @param brandId - The ID of the brand to fetch Facebook pages for
 * @returns Promise containing the Facebook pages data
 * @throws Error if the API request fails
 */
export const getFacebookPages_api = async (brandId: number, fields: string = 'name,category,id,access_token,followers_count,picture{url},is_published'): Promise<{ data: FacebookPageType[] }> => {
  try {
    const response = await Axios.get<{ data: FacebookPageType[] }>(`/facebook/${brandId}/pages`, {
      params: {
        fields: fields,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Facebook pages:', error);
    throw error;
  }
};

export const getInstagramAccountFromFbPage_api = async (selectedFBPage: FacebookPageType, access_token?: string) => {
  try {
    let fbAccountDetails: FacebookPageType = selectedFBPage;
    if (!selectedFBPage.access_token) fbAccountDetails = await getFbAccountDetails(selectedFBPage, access_token)
    const instagramAccount = await queryFB(
      `/${fbAccountDetails.id}/instagram_accounts`,
      {
        params: {
          fields:
            'id,follow_count,followed_by_count,username,profile_picture_url,is_published',
          access_token: fbAccountDetails?.access_token,
          forceToken: fbAccountDetails?.access_token,
        },
      }
    );

    const businessAccounts: any = await queryFB(`/${fbAccountDetails.id}`, {
      params: {
        fields: 'name,category,id,access_token,followers_count,picture{url},is_published,instagram_business_account{username,id,followers_count,profile_picture_url},connected_instagram_account{username,id,followers_count,profile_picture_url}',
        access_token,
      },
    });

    if (!businessAccounts || !instagramAccount?.data?.length) return null;

    const { connected_instagram_account, instagram_business_account } =
      businessAccounts;

    const response: InstagramPageType = {
      id: instagramAccount.data[0].id,
      name: businessAccounts.name,
      businessAccount: instagram_business_account,
      connected_instagram_account,
      instagramAccount: instagramAccount.data[0],
      url: instagramAccount.data[0].url,
    };

    return response;
  } catch (error) {
    console.error('Error fetching Instagram account:', error);
    return null;
  }
};

export const isValidAccessToken_api = async (accessToken: string) => {
  try {
    const response = await queryFB(`/me`, {
      method: 'get',
      params: {
        access_token: accessToken,
      },
    });
    return response.id && response.name ? true : false
  } catch (error) {
    Logger.error('Error validating access token', error)
    return false
  }
};

export const getFacebookImage_api = async (page: string, access_token?: string) => {
  try {
    const response: any = await queryFB(`/${page}`, {
      params: {
        fields: 'id,name,picture',
        access_token,
      },
    });
    return response.picture.data.url;
  } catch (error) {
    Logger.error('Error fetching Instagram image', error)
    return null
  }
}
/** Parameters for searching Facebook ad interests */
interface SearchAdInterestsParams {
  /** Search query string */
  query: string;
  /** Maximum number of results to return */
  limit?: number;
}
/** Search for Facebook ad interests */
export const searchFacebookAdInterests = async ({ query, limit = 10 }: SearchAdInterestsParams): Promise<DataFBQueryAdInterest> => {
  try {
    // First search for ad interests
    const interestResults = await queryFB('/search', {
      params: {
        type: 'adinterest',
        q: query,
        limit,
      },
    })

    const interestData = interestResults.data as FacebookInterestSearchData[]
    const interestNames = interestData.filter((o: FacebookInterestSearchData) => o.interests).map((o: FacebookInterestSearchData) => o.interests)

    // Then validate the interests 
    const validationResults = await queryFB('/search', {
      params: {
        type: 'adinterestvalid',
        interest_list: interestNames,
      },
    })

    return validationResults.data.filter((o: FacebookInterestSearchData) => o.interests)
  } catch (error) {
    throw error
  }
}

export const searchInstagramPosts_api = async ({
  brandId,
  query
}: {
  brandId: number,
  query: string
}) => {
  try {
    const response = await Axios.get<InstagramPost[]>(`/facebook/${brandId}/search-instagram-posts`, {
      params: {
        query
      }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

/** Search for Facebook interests using Maestro recommendations */
export const searchMaestroFacebookInterests_api = async (brandId: number, query: string): Promise<FacebookInterestSearchData> => {
  try {
    const response = await Axios.get<FacebookInterestSearchData>(`/facebook/${brandId}/maestro-recommendations`, {
      params: {
        query
      }
    })
    return response.data
  } catch (error) {
    throw error
  }

}

export interface SpotifyArtistSearchResponse {
  id: string,
  name: string,
  followers: number,
  genres: string[],
  thumbnail_url: string
}

/** Search for Facebook interests using Maestro recommendations */
export const searchSimilarArtists_api = async (brandId: number, query: string): Promise<SpotifyArtistSearchResponse[]> => {
  try {
    const response = await Axios.get<SpotifyArtistSearchResponse[]>(`/spotify/search/artists`, {
      params: {
        q: query,
        limit: 10
      }
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getFacebookPixelsForAdAccount = async (fbAdAccount: AdAccountPageType): Promise<FBPixel[]> => {

  try {
    const results: {

      data: FBPixel[], paging: { cursors?: { before: string, after: string } }

    } = await queryFB(`/${fbAdAccount.id}/adspixels`, {
      params: {
        fields: 'id,name,creation_time,owner_business,stats{data}',
      },
    });

    return results.data
  } catch (error) {
    throw error;
  }
};

export const getConversionEventsForPixel = async (fbPixel: FBPixel): Promise<{
  start_time: string;
  aggregation: string;
  data: { value: string; count: number; }[];
}[]> => {

  try {
    const results: {

      data: {
        start_time: string;
        aggregation: string;
        data: { value: string; count: number; }[];
      }[], paging: { cursors?: { before: string, after: string } }

    } = await queryFB(`/${fbPixel.id}/stats`, {
      params: {
        fields: 'data',
        aggregation: 'event',
        start_time: dayjs().subtract(30, 'days').unix().toString(),
      },
    });

    return results.data
  } catch (error) {
    throw error;
  }
};


/** Interface for Facebook location search response */
export interface FBGeographySearchResponse {
  data: FBLocation[]
  paging?: {
    cursors?: {
      before: string
      after: string
    }
  }
}

/**
 * Searches for geographical locations (countries, regions, cities) on Facebook Ads
 * @param query - Search query string
 * @param accessToken - Facebook access token
 * @returns Promise containing array of matching locations
 */
export const searchFacebookGeographies = async (
  query: string,
  accessToken: string
): Promise<FBGeographySearchResponse> => {
  if (!query) {
    return { data: [] }
  }

  return queryFB('/search', {
    method: 'GET',
    params: {
      access_token: accessToken,
      limit: 10,
      type: 'adgeolocation',
      location_types: '["country","region","city"]',
      q: query
    }
  })
}

export const getLocales = async ({
  locales = '',
  accessToken
}: {
  locales?: string,
  accessToken?: string
}): Promise<{
  key: number,
  name: string
}[]> => {
  const response = await queryFB(`/search`, {
    params: {
      access_token: accessToken,
      type: 'adlocale',
      q: locales,
      limit: 1000
    }
  });

  return response.data
}
